import { useAuth } from '@/contexts/auth-context';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import { mcpQueryKeys } from '@/hooks/queries/use-mcp-query';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import type { ApiResponse, OrganizationBillingContext } from '@fluenceur/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const PAID_CHECKOUT_STATUS_QUERY_KEY = ['paidCheckoutStatus'] as const;

/** A pure read of an operation already identified by the scoped server context. */
export function usePaidCheckoutStatusQuery({
  context,
  enabled = true,
  returnTo,
}: {
  context?: OrganizationBillingContext;
  enabled?: boolean;
  returnTo?: string;
}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingActionScope();
  const client = useQueryClient();
  const canReadOperation =
    !!context &&
    !!readBillingContext(context) &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    !!context.paidCheckout &&
    context.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation';
  const operationId = canReadOperation ? context.paidCheckout?.id : null;
  const query = useQuery<OrganizationBillingContext>({
    queryKey: [...PAID_CHECKOUT_STATUS_QUERY_KEY, scope.organizationId, scope.brandId, operationId],
    enabled: enabled && !isLoading && canReadOperation,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      assertCurrent(scope);
      if (!canReadOperation || !operationId) throw new Error('No authorized paid checkout');
      const response = await api.get<ApiResponse<unknown>>(
        `/dashboard/billing/paid-checkout/${encodeURIComponent(operationId)}`,
        { signal }
      );
      assertCurrent(scope);
      const next = readBillingContext(response.data.data);
      if (
        response.data.success !== true ||
        !next ||
        next.organizationId !== scope.organizationId ||
        next.brandId !== scope.brandId
      ) {
        throw new Error('Invalid paid checkout context');
      }
      const contextKey = billingContextQueryKey(scope);
      await client.cancelQueries({ queryKey: contextKey, exact: true });
      assertCurrent(scope);
      client.setQueryData(contextKey, next);
      if (
        next.access.allowed &&
        next.accessSource === 'subscription' &&
        next.subscription?.status === 'active'
      ) {
        const hint = getMcpReturnTo(returnTo);
        const transaction = hint?.startsWith('/mcp/authorize?')
          ? new URLSearchParams(hint.split('?')[1]).get('transaction')
          : null;
        assertCurrent(scope);
        await Promise.all([
          client.invalidateQueries({ queryKey: planFeaturesQueryKey, exact: true }),
          client.invalidateQueries({
            queryKey: [...SUBSCRIPTION_QUERY_KEY, scope.brandId],
            exact: true,
          }),
          ...(transaction
            ? [
                client.invalidateQueries({
                  queryKey: mcpQueryKeys.consent(transaction),
                  exact: true,
                }),
              ]
            : []),
        ]);
        assertCurrent(scope);
      }
      return next;
    },
  });
  return { ...query, canReadOperation };
}
